import polygonClipping from 'polygon-clipping';
import { createId } from '../core/model';
import { IDENTITY_MATRIX, transformPoint, type Matrix, type Point } from '../core/geometry';
import type { MultiPolygon, Pair, Polygon, Ring } from 'polygon-clipping';
import type { PathNode, PathObject, VectorObject } from './types';
import { effectiveObjectPathGeometry } from './path';

export type VectorBooleanOperation = 'unite' | 'subtract' | 'intersect' | 'exclude';

function midpoint(left: Point, right: Point): Point {
  return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
}

function pointLineDistance(point: Point, start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 0.000001) return Math.hypot(point.x - start.x, point.y - start.y);
  return Math.abs((dy * point.x) - (dx * point.y) + (end.x * start.y) - (end.y * start.x)) / length;
}

function flattenCubic(start: Point, control1: Point, control2: Point, end: Point, output: Point[], depth = 0): void {
  if (depth >= 12 || Math.max(pointLineDistance(control1, start, end), pointLineDistance(control2, start, end)) <= 0.25) {
    output.push(end);
    return;
  }
  const a = midpoint(start, control1);
  const b = midpoint(control1, control2);
  const c = midpoint(control2, end);
  const d = midpoint(a, b);
  const e = midpoint(b, c);
  const center = midpoint(d, e);
  flattenCubic(start, a, d, center, output, depth + 1);
  flattenCubic(center, e, c, end, output, depth + 1);
}

function closeRing(points: readonly Point[]): Ring | null {
  const compact: Pair[] = [];
  for (const point of points) {
    const rounded: Pair = [Math.round(point.x * 1_000_000) / 1_000_000, Math.round(point.y * 1_000_000) / 1_000_000];
    const previous = compact.at(-1);
    if (!previous || previous[0] !== rounded[0] || previous[1] !== rounded[1]) compact.push(rounded);
  }
  if (compact.length < 3) return null;
  const first = compact[0]!;
  const last = compact.at(-1)!;
  if (first[0] !== last[0] || first[1] !== last[1]) compact.push([...first]);
  return compact.length >= 4 ? compact : null;
}

function flattenPath(object: Extract<VectorObject, { type: 'path' }>, transform: Matrix): Ring[] {
  const geometry = effectiveObjectPathGeometry(object);
  const nodes = new Map(geometry.nodes.map((node) => [node.id, node]));
  return geometry.subpaths.flatMap((subpath) => {
    if (!subpath.closed) return [];
    const ordered = subpath.nodeIds.flatMap((id) => {
      const node = nodes.get(id);
      return node ? [node] : [];
    });
    const first = ordered[0];
    if (!first) return [];
    const output = [transformPoint(transform, first.anchor)];
    for (let index = 1; index < ordered.length; index += 1) {
      const previous = ordered[index - 1];
      const node = ordered[index];
      if (!previous || !node) continue;
      const start = transformPoint(transform, previous.anchor);
      const end = transformPoint(transform, node.anchor);
      if (previous.outgoing || node.incoming) {
        flattenCubic(
          start,
          transformPoint(transform, previous.outgoing ?? previous.anchor),
          transformPoint(transform, node.incoming ?? node.anchor),
          end,
          output,
        );
      } else output.push(end);
    }
    const last = ordered.at(-1);
    if (last && (last.outgoing || first.incoming)) {
      flattenCubic(
        transformPoint(transform, last.anchor),
        transformPoint(transform, last.outgoing ?? last.anchor),
        transformPoint(transform, first.incoming ?? first.anchor),
        transformPoint(transform, first.anchor),
        output,
      );
    }
    const ring = closeRing(output);
    return ring ? [ring] : [];
  });
}

function ellipseRing(object: Extract<VectorObject, { type: 'ellipse' }>, transform: Matrix): Ring[] {
  const { x, y, width, height } = object.geometry;
  const segments = Math.max(24, Math.min(192, Math.ceil(Math.PI * Math.sqrt(Math.abs(width * height)) / 6)));
  const points = Array.from({ length: segments }, (_, index) => {
    const angle = index / segments * Math.PI * 2;
    return transformPoint(transform, {
      x: x + width / 2 + Math.cos(angle) * width / 2,
      y: y + height / 2 + Math.sin(angle) * height / 2,
    });
  });
  const ring = closeRing(points);
  return ring ? [ring] : [];
}

function rectangleRing(object: Extract<VectorObject, { type: 'rectangle' }>, transform: Matrix): Ring[] {
  const { x, y, width, height, radius } = object.geometry;
  const maximum = Math.max(0, Math.min(Math.abs(width), Math.abs(height)) / 2);
  const topRight = Math.min(maximum, Math.max(0, radius.topRight));
  const bottomRight = Math.min(maximum, Math.max(0, radius.bottomRight));
  const bottomLeft = Math.min(maximum, Math.max(0, radius.bottomLeft));
  const topLeft = Math.min(maximum, Math.max(0, radius.topLeft));
  const corners = [
    { x: x + width - topRight, y: y + topRight, radius: topRight, start: -Math.PI / 2 },
    { x: x + width - bottomRight, y: y + height - bottomRight, radius: bottomRight, start: 0 },
    { x: x + bottomLeft, y: y + height - bottomLeft, radius: bottomLeft, start: Math.PI / 2 },
    { x: x + topLeft, y: y + topLeft, radius: topLeft, start: Math.PI },
  ];
  const points: Point[] = [];
  for (const corner of corners) {
    const steps = corner.radius > 0 ? 6 : 1;
    for (let step = 0; step <= steps; step += 1) {
      const angle = corner.start + step / steps * Math.PI / 2;
      points.push(transformPoint(transform, {
        x: corner.x + Math.cos(angle) * corner.radius,
        y: corner.y + Math.sin(angle) * corner.radius,
      }));
    }
  }
  const ring = closeRing(points);
  return ring ? [ring] : [];
}

function signedArea(ring: Ring): number {
  let area = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const point = ring[index];
    const next = ring[index + 1];
    if (point && next) area += point[0] * next[1] - next[0] * point[1];
  }
  return area / 2;
}

function pointInRing(point: Pair, ring: Ring): boolean {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const a = ring[index];
    const b = ring[previous];
    if (!a || !b) continue;
    if ((a[1] > point[1]) !== (b[1] > point[1]) && point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

/** Groups even/odd contours into polygon-clipping's outer-ring-plus-holes structure. */
function ringsToMultiPolygon(rings: Ring[]): MultiPolygon {
  const ordered = [...rings].sort((left, right) => Math.abs(signedArea(right)) - Math.abs(signedArea(left)));
  const polygons: Polygon[] = [];
  const outerRings: Ring[] = [];
  for (const ring of ordered) {
    const probe = ring[0];
    if (!probe) continue;
    const containing = outerRings.map((outer, index) => ({ outer, index })).filter(({ outer }) => pointInRing(probe, outer));
    if (containing.length % 2 === 0) {
      outerRings.push(ring);
      polygons.push([ring]);
    } else {
      const owner = containing.at(-1);
      if (owner) polygons[owner.index]?.push(ring);
    }
  }
  return polygons;
}

export function flattenVectorObject(object: VectorObject): MultiPolygon {
  const rings = object.type === 'path'
    ? flattenPath(object, object.transform)
    : object.type === 'ellipse'
      ? ellipseRing(object, object.transform)
      : rectangleRing(object, object.transform);
  return ringsToMultiPolygon(rings);
}

function multiPolygonToPath(source: VectorObject, polygons: MultiPolygon, operation: VectorBooleanOperation): PathObject | null {
  const nodes: PathNode[] = [];
  const subpaths: PathObject['geometry']['subpaths'][number][] = [];
  for (const polygon of polygons) for (const ring of polygon) {
    const coordinates = ring.length > 1 && ring[0]?.[0] === ring.at(-1)?.[0] && ring[0]?.[1] === ring.at(-1)?.[1] ? ring.slice(0, -1) : ring;
    if (coordinates.length < 3) continue;
    const subpathId = createId('subpath');
    const ringNodes = coordinates.map(([x, y]): PathNode => ({
      id: createId('node'),
      subpathId,
      anchor: { x, y },
      incoming: null,
      outgoing: null,
      continuity: 'corner',
    }));
    nodes.push(...ringNodes);
    subpaths.push({ id: subpathId, nodeIds: ringNodes.map((node) => node.id), closed: true });
  }
  if (!subpaths.length) return null;
  return {
    ...structuredClone(source),
    id: createId('vector'),
    name: `${operation[0]?.toUpperCase() ?? ''}${operation.slice(1)}`,
    type: 'path',
    transform: IDENTITY_MATRIX,
    geometry: { nodes, subpaths, fillRule: 'evenodd' },
  };
}

export function combineVectorObjects(objects: readonly VectorObject[], operation: VectorBooleanOperation): PathObject | null {
  if (objects.length < 2) return null;
  const geometries = objects.map(flattenVectorObject).filter((geometry) => geometry.length > 0);
  const styleSource = objects.at(-1);
  const first = geometries[0];
  if (!styleSource || !first || geometries.length !== objects.length) return null;
  let result: MultiPolygon;
  if (operation === 'unite') result = polygonClipping.union(first, ...geometries.slice(1));
  else if (operation === 'intersect') result = polygonClipping.intersection(first, ...geometries.slice(1));
  else if (operation === 'exclude') result = polygonClipping.xor(first, ...geometries.slice(1));
  else result = polygonClipping.difference(first, ...geometries.slice(1));
  return multiPolygonToPath(styleSource, result, operation);
}
